import {Box} from '@mui/material';
import {useContext, useState, MutableRefObject, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams, useRouteMatch} from 'react-router-dom';
import {PlaylistItem} from '../../../../../../../backend/interfaces/playlist-item';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import SearchInput from '../../../../../../../components/search-input/search-input';
import {AuthContext} from '../../../../../../../contexts/auth/context';
import {AuthContextType} from '../../../../../../../contexts/auth/types';
import {useOffice} from '../../../../../../../contexts/office/context';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';

// Columns IDs
enum ColumnIdentifier {
  Title = 'title',
  State = 'state',
}

export default function PlaylistItemsProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  // Get params
  let {entityId, playlistId}: {entityId?: string; playlistId?: string} = useParams();

  // Get context
  const authContext: AuthContextType = useContext(AuthContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Hold state
  const [updateItemDialogOpened, setUpdateItemDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {itemId: string; itemName: string; itemActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();
  const officeContext = useOffice();

  const baseUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`;

  // Fetch playlist items
  const handleGetPlaylistItems = async (limit: number, skip: number, search?: string) => {
    // Check entity id and playlist id
    if (!entityId || !playlistId) {
      // Error
      throw new Error('Empty entity or playlist id.');
    }

    // Load playlist items
    const result: {totalCount: number; data: Array<PlaylistItem>} =
      await PlaylistsRepository.instance.getPlaylistItemsInEntityAsAffiliatedForEntity(
        entityId,
        playlistId,
        skip,
        limit,
        true,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistItem> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.thumbnailUrl,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Title,
            // The title is localized
            value: obj.title || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.playlists.professional_caregiver.edit.items.state_value_true)
              : t(translations.playlists.professional_caregiver.edit.items.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handleUpdateCurrentSearch,
    handlePageChange,
    handleUpdateItem,
    handleApplyFilters,
  } = usePaginatedTable<PlaylistItem>({
    fetchData: handleGetPlaylistItems,
  });

  const handleAskUpdateItemState = (itemId: string, value: boolean) => {
    // Get item name
    const itemName: string | undefined = data
      .find((obj) => obj.id === itemId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Title)?.value;

    // Set item info for later
    dataToUpdateRef.current = {
      itemId: itemId,
      itemActiveFlag: value,
      itemName: itemName || '',
    };

    // Open modal
    setUpdateItemDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdateItemState = () => {
    // Close modal
    setUpdateItemDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateItemState = () => {
    // Close modal
    setUpdateItemDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdateItemState();
  };

  // Enable/disable item
  const handleUpdateItemState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const itemId = dataToUpdateRef.current.itemId;
      const value = dataToUpdateRef.current.itemActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === itemId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        itemId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.playlists.professional_caregiver.edit.items.state_value_true)
              : t(translations.playlists.professional_caregiver.edit.items.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it can take some time to update
      continueUpdateItemState(entityId || '', playlistId || '', itemId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.playlists.professional_caregiver.edit.items.errors
            .update_state_item_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist item. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateItemState = async (
    entityId: string,
    playlistId: string,
    itemId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistItemActiveStateForAllUsersAsAffiliatedForEntity(
        entityId,
        playlistId,
        itemId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === itemId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        itemId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.playlists.professional_caregiver.edit.items.state_value_true)
              : t(translations.playlists.professional_caregiver.edit.items.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.playlists.professional_caregiver.edit.items.errors
                .enable_item_error_message
            )
          : t(
              translations.playlists.professional_caregiver.edit.items.errors
                .disable_item_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist item with id ${itemId} to ${value}.`,
        error
      );
    }
  };

  return (
    <>
      <GenericHeader
        isLoading={!officeContext.state.playlistForm}
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: baseUrl,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.playlists),
            link: `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}`,
          },
          {
            label: officeContext.state.playlistForm?.name || '',
            link: `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${playlistId}`,
          },
          {
            label: t(translations.playlists.professional_caregiver.edit.tabs.items),
            link: url,
          },
        ]}
        leftColumn={
          <>
            <SearchInput
              placeholder={t(
                translations.playlists.professional_caregiver.root.search_input_placeholder
              )}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </>
        }
        rightColumn={null}
      />

      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'other',
            }}
            emptyTableMessage={t(translations.playlists.professional_caregiver.edit.items.no_items)}
            columns={[
              {
                columnId: ColumnIdentifier.Title,
                title: t(translations.playlists.professional_caregiver.edit.items.title_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.playlists.professional_caregiver.edit.items.state_header),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(
                      translations.playlists.professional_caregiver.edit.items.state_value_true
                    ),
                    handleClick: handleAskUpdateItemState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_playlist_items.state_disabled_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
          <AlertModal
            id="confirm-disable-playlist-item-state-entity-users"
            opened={updateItemDialogOpened.disableDialogOpened}
            title={t(
              translations.playlists.professional_caregiver.edit.items.disable_item_dialog_title,
              {
                name: dataToUpdateRef.current?.itemName || '',
              }
            )}
            content={t(
              translations.playlists.professional_caregiver.edit.items.disable_item_dialog_message,
              {
                name: authContext.state.session?.selectedAffiliation?.entity.name || '',
              }
            )}
            handleClose={handleDeclineUpdateItemState}
            actions={[
              {
                label: t(
                  translations.playlists.professional_caregiver.edit.items
                    .disable_item_dialog_action_yes_button
                ),
                type: 'destructive',
                handleClick: handleConfirmUpdateItemState,
              },
              {
                label: t(
                  translations.playlists.professional_caregiver.edit.items
                    .disable_item_dialog_action_no_button
                ),
                type: 'neutral',
                handleClick: handleDeclineUpdateItemState,
              },
            ]}
          />
        </Box>
      </Box>
      <AlertModal
        id="confirm-enable-playlist-item-state-entity-users"
        opened={updateItemDialogOpened.enableDialogOpened}
        title={t(
          translations.playlists.professional_caregiver.edit.items.enable_item_dialog_title,
          {
            name: dataToUpdateRef.current?.itemName || '',
          }
        )}
        content={t(
          translations.playlists.professional_caregiver.edit.items.enable_item_dialog_message,
          {
            name: authContext.state.session?.selectedAffiliation?.entity.name || '',
          }
        )}
        handleClose={handleDeclineUpdateItemState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.edit.items
                .enable_item_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdateItemState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.edit.items
                .enable_item_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdateItemState,
          },
        ]}
      />
    </>
  );
}
